import { Component } from '@theme/component';
import { onAnimationEnd, prefersReducedMotion } from '@theme/utilities';

/**
 * Custom element for the blog article image gallery lightbox.
 *
 * Intercepts clicks on .js-gallery-lightbox anchors and opens a <dialog>
 * modal with the full-resolution image. Supports keyboard navigation
 * (Escape, ArrowLeft, ArrowRight), prev/next buttons, and touch swipe.
 */
class BlogGallery extends Component {
  /** @type {{ src: string; alt: string }[]} */
  #images = [];
  #currentIndex = 0;
  /** @type {HTMLDialogElement} */
  #dialog;
  /** @type {HTMLImageElement} */
  #img;
  /** @type {HTMLButtonElement} */
  #prevBtn;
  /** @type {HTMLButtonElement} */
  #nextBtn;
  #previousScrollY = 0;
  #touchStartX = 0;
  #touchStartY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#buildDialog();
    this.#collectImages();
    this.addEventListener('click', this.#handleItemClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleItemClick);
    this.#dialog?.remove();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#collectImages();
  }

  #collectImages() {
    this.#images = Array.from(this.querySelectorAll('.js-gallery-lightbox')).map((a) => ({
      src: a.getAttribute('data-full') || a.getAttribute('href') || '',
      alt: a.querySelector('img')?.alt || '',
    }));
  }

  #buildDialog() {
    const dialog = document.createElement('dialog');
    dialog.className = 'blog-gallery-lightbox';
    dialog.innerHTML = `
      <div class="blog-gallery-lightbox__inner">
        <button class="blog-gallery-lightbox__close" aria-label="Chiudi" type="button">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12"/>
          </svg>
        </button>
        <button class="blog-gallery-lightbox__nav blog-gallery-lightbox__nav--prev" aria-label="Immagine precedente" type="button" hidden>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <img class="blog-gallery-lightbox__img" src="" alt="" />
        <button class="blog-gallery-lightbox__nav blog-gallery-lightbox__nav--next" aria-label="Immagine successiva" type="button" hidden>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      </div>
    `;

    this.#img = /** @type {HTMLImageElement} */ (dialog.querySelector('.blog-gallery-lightbox__img'));
    this.#prevBtn = /** @type {HTMLButtonElement} */ (dialog.querySelector('.blog-gallery-lightbox__nav--prev'));
    this.#nextBtn = /** @type {HTMLButtonElement} */ (dialog.querySelector('.blog-gallery-lightbox__nav--next'));

    dialog.querySelector('.blog-gallery-lightbox__close').addEventListener('click', this.close);
    this.#prevBtn.addEventListener('click', this.prev);
    this.#nextBtn.addEventListener('click', this.next);
    dialog.addEventListener('click', this.#handleOverlayClick);
    dialog.addEventListener('keydown', this.#handleKeyDown);
    dialog.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    dialog.addEventListener('touchend', this.#handleTouchEnd, { passive: true });

    this.#dialog = dialog;
    this.append(dialog);
  }

  #handleItemClick = (event) => {
    const anchor = /** @type {Element} */ (event.target)?.closest?.('.js-gallery-lightbox');
    if (!anchor) return;

    event.preventDefault();
    const anchors = Array.from(this.querySelectorAll('.js-gallery-lightbox'));
    const index = anchors.indexOf(anchor);
    this.#open(index >= 0 ? index : 0);
  };

  #open(index) {
    this.#dialog.classList.remove('blog-gallery-lightbox--closing');
    this.#currentIndex = index;
    this.#updateNav();
    this.#previousScrollY = window.scrollY;

    const { src, alt } = this.#images[index];
    this.#img.alt = alt;

    const show = () => {
      requestAnimationFrame(() => {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.#previousScrollY}px`;
        this.#dialog.showModal();
      });
    };

    // Cancella handler di un open precedente interrotto (click rapido)
    this.#img.onload = null;
    this.#img.onerror = null;

    this.#img.onload = () => {
      this.#img.onload = null;
      this.#img.onerror = null;
      show();
    };
    this.#img.onerror = () => {
      this.#img.onload = null;
      this.#img.onerror = null;
      show(); // apre comunque anche se l'immagine non carica
    };
    this.#img.src = src;

    // Cache hit: alcuni browser impostano complete = true in modo sincrono
    if (this.#img.complete) {
      this.#img.onload = null;
      this.#img.onerror = null;
      show();
    }
  }

  close = async () => {
    if (!this.#dialog.open) return;

    if (!prefersReducedMotion()) {
      this.#dialog.classList.add('blog-gallery-lightbox--closing');
      void this.#dialog.offsetWidth; // forza style recalc prima di leggere getAnimations()
      await onAnimationEnd(this.#dialog, undefined, { subtree: true });
    }

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.#dialog.close();
    // Rimosso DOPO dialog.close(): evita che [open] riattivi blog-lightbox-in
    this.#dialog.classList.remove('blog-gallery-lightbox--closing');
  };

  prev = () => {
    if (this.#images.length <= 1) return;
    this.#currentIndex = (this.#currentIndex - 1 + this.#images.length) % this.#images.length;
    this.#updateImage();
  };

  next = () => {
    if (this.#images.length <= 1) return;
    this.#currentIndex = (this.#currentIndex + 1) % this.#images.length;
    this.#updateImage();
  };

  #updateImage() {
    const { src, alt } = this.#images[this.#currentIndex];
    this.#img.src = src;
    this.#img.alt = alt;
  }

  #updateNav() {
    const multiple = this.#images.length > 1;
    this.#prevBtn.hidden = !multiple;
    this.#nextBtn.hidden = !multiple;
  }

  #handleOverlayClick = (event) => {
    const inner = this.#dialog.querySelector('.blog-gallery-lightbox__inner');
    if (inner && !inner.contains(/** @type {Node} */ (event.target))) {
      this.close();
    }
  };

  #handleKeyDown = (event) => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowLeft':
        this.prev();
        break;
      case 'ArrowRight':
        this.next();
        break;
    }
  };

  #handleTouchStart = (event) => {
    this.#touchStartX = event.touches[0].clientX;
    this.#touchStartY = event.touches[0].clientY;
  };

  #handleTouchEnd = (event) => {
    const deltaX = event.changedTouches[0].clientX - this.#touchStartX;
    const deltaY = event.changedTouches[0].clientY - this.#touchStartY;

    // Ignora swipe prevalentemente verticali
    if (Math.abs(deltaY) > Math.abs(deltaX)) return;
    if (Math.abs(deltaX) < 50) return;

    if (deltaX < 0) {
      this.next();
    } else {
      this.prev();
    }
  };
}

if (!customElements.get('blog-gallery')) {
  customElements.define('blog-gallery', BlogGallery);
}
